import React, {RefObject} from 'react';
import {observer} from 'mobx-react';
import {configStore, cupController, cupStore} from 'app/store/global';
import {openAuth, openRegistration} from 'app/components/auth/auth';
import {getLinkPath} from 'app/utils';
import I18n from 'app/i18n';
import type {CupUserTokenPayload} from 'app/store/CupStore';
import {startSteamAuthFlow} from 'app/utils/steamLink';

interface State {
    lobbyUnavailable: boolean
}

interface CupMessage {
    type?: 'cup:request-user-token' | 'cup:require-login' | 'cup:route' | 'cup:resize' |
        'cup:scroll-to-top' | 'cup:link-steam'
    hash?: string
    height?: number
    tab?: 'login' | 'register'
}

const ISO_DATE_LENGTH = 10;
const LOBBY_UNAVAILABLE_TIMEOUT_MS = 15000;
const MIN_LOBBY_HEIGHT_PX = 720;

@observer
class EgbCup extends React.Component<unknown, State> {
    iframeRef: RefObject<HTMLIFrameElement> = React.createRef();

    private lobbyAvailabilityTimeoutId: number | null = null;

    constructor(props: unknown) {
        super(props);

        this.state = {lobbyUnavailable: false};
    }

    componentDidMount(): void {
        window.addEventListener('message', this.handleMessage);
        window.addEventListener('popstate', this.handlePopState);
        this.scheduleLobbyAvailabilityCheck();
    }

    componentWillUnmount(): void {
        window.removeEventListener('message', this.handleMessage);
        window.removeEventListener('popstate', this.handlePopState);
        this.clearLobbyAvailabilityCheck();
    }

    buildIframeSrc = (): string => {
        const params = new URLSearchParams({
            _v: new Date().toISOString().slice(0, ISO_DATE_LENGTH),
            balance: cupStore.balance,
            centrifugoUrl: cupStore.centrifugoPath,
            countryCode: cupStore.countryCode,
            currency: cupStore.currency,
            email: cupStore.email,
            embeddedInWidget: 'true',
            externalId: cupStore.currentExternalId,
            lang: cupStore.lang,
            nickname: cupStore.nickname,
            parentOrigin: cupStore.parentOrigin,
            steamId64: cupStore.steamId64,
            timezone: cupStore.timezone,
            userToken: cupStore.userToken
        });

        return `${cupStore.lobbyUrl}?${params.toString()}${window.location.hash || ''}`;
    };

    postUserToken = ({userToken, nickname, countryCode, email, balance, currency, steamId64}: CupUserTokenPayload): void => {
        this.iframeRef.current?.contentWindow?.postMessage(
            {balance, countryCode, currency, email, nickname, steamId64, type: 'cup:user-token', userToken},
            cupStore.lobbyOrigin
        );
    };

    requestUserTokenRefresh = (): void => {
        cupController.refreshUserToken()
            .then(this.postUserToken)
            .catch(() => null);
    };

    currentCupPath = (): string => getLinkPath(`/cup${window.location.hash || ''}`);

    handleMessage = (event: MessageEvent<CupMessage>): void => {
        if (event.source !== this.iframeRef.current?.contentWindow) {
            return;
        }

        this.clearLobbyAvailabilityCheck();

        if (this.state.lobbyUnavailable) {
            this.setState({lobbyUnavailable: false});
        }

        const {data} = event;

        switch (data?.type) {
        case 'cup:request-user-token':
            this.requestUserTokenRefresh();
            break;
        case 'cup:require-login':
            if (data.tab === 'register') {
                openRegistration('CUP', this.currentCupPath());
            } else {
                openAuth(this.currentCupPath());
            }
            break;
        case 'cup:route':
            if (typeof data.hash === 'string') {
                history.replaceState(null, '', getLinkPath(`/cup${data.hash}`));
            }
            break;
        case 'cup:link-steam':
            startSteamAuthFlow();
            break;
        case 'cup:resize':
            if (typeof data.height === 'number' && data.height > 0) {
                const iframe = this.iframeRef.current;

                if (iframe) {
                    iframe.style.height = `${Math.max(data.height, MIN_LOBBY_HEIGHT_PX)}px`;
                }
            }
            break;
        case 'cup:scroll-to-top':
            window.scrollTo(0, 0);
            break;
        default:
            break;
        }
    };

    handlePopState = (): void => {
        this.iframeRef.current?.contentWindow?.postMessage(
            {hash: window.location.hash || '#/', type: 'cup:navigate'},
            cupStore.lobbyOrigin
        );
    };

    scheduleLobbyAvailabilityCheck = (): void => {
        if (this.lobbyAvailabilityTimeoutId !== null) {
            return;
        }

        this.lobbyAvailabilityTimeoutId = window.setTimeout(() => {
            this.lobbyAvailabilityTimeoutId = null;
            this.setState({lobbyUnavailable: true});
        }, LOBBY_UNAVAILABLE_TIMEOUT_MS);
    };

    clearLobbyAvailabilityCheck = (): void => {
        if (this.lobbyAvailabilityTimeoutId !== null) {
            window.clearTimeout(this.lobbyAvailabilityTimeoutId);
            this.lobbyAvailabilityTimeoutId = null;
        }
    };

    handleReloadPage = (): void => {
        window.location.reload();
    };

    renderUnavailableStub(): React.ReactNode {
        return <div className="cup-lobby-card">
            <p className="cup-lobby-card__text">{I18n.t('poker.proom_unavailable')}</p>
            <button
                className="cup-lobby-card__reload-button"
                onClick={this.handleReloadPage}
                type="button"
            >
                {I18n.t('poker.reload_page')}
            </button>
        </div>;
    }

    render(): React.ReactNode {
        if (!configStore.isReady) {
            return null;
        }

        if (this.state.lobbyUnavailable) {
            return this.renderUnavailableStub();
        }

        return <div className="cup-page">
            <iframe
                allow="clipboard-write"
                className="cup-page__lobby-iframe"
                id="cup-lobby"
                ref={this.iframeRef}
                scrolling="no"
                src={this.buildIframeSrc()}
                title="cup-lobby"
            />
        </div>;
    }
}

export default EgbCup;
